<script setup>
import {
  computed,
  ref,
  watch
} from 'vue'
import { useStore } from 'vuex'
import {
  KkModal,
  KkButton,
  KkLoading
} from '../../../base'
import { KkTabList } from '../../../../components/ui'
import {
  KkFacebookPreview,
  KkInstagramPreview,
  KkTwitterPreview,
  KkAccountContainer
} from '../../../../components/domains/sns'
import moment from 'moment'
import { useI18n } from 'vue-i18n'

const store = useStore()
const { t, locale } = useI18n()

const props = defineProps({
  isPreview: {
    type: Boolean,
    default: false
  },
  showActionModal: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['close-action-modal', 'new-post-submit', 'post-approval-submit'])

const approvalRequestNotes = ref('')
const selectedSnsType = ref('')
const post = computed(() => store.getters['companiesStoreSnsPosts/post'])
const previewLoading = computed(() => store.getters['companiesStoreSnsPosts/previewLoading'])
const reservationDateTime = computed(() => store.getters['companiesStoreSnsPosts/reservationDateTime'])
const submitActionModalType = computed(() => store.getters['companiesStoreSnsPosts/submitActionModalType'])
const selectedPostSnsType = computed(() =>{
  return store.getters['companiesStoreSnsPosts/selectedPostSnsType'] || post.value?.kind?.toUpperCase()
})
const updateApprovalRequestNotes = (payload) => store.dispatch('companiesStoreSnsPosts/updateApprovalRequestNotes', payload)

const scheduleDateTime = computed(() => moment(reservationDateTime.value).format(t('format.datetime.includeWday')))
const postTiming = computed(() => store.getters['companiesStoreSnsPosts/postTiming'])

const submitButtonText = computed(() => {
  let text
  switch (submitActionModalType.value) {
  case 'post':
    text = t('pages.CompaniesStorePost.post')
    break
  case 'draft':
    text = t('pages.CompaniesStorePost.draft')
    break
  case 'request':
    text = t('pages.CompaniesStorePost.apply')
    break
  default:
    text = ''
    break
  }
  return text
})

const previewSnsTypes = computed(() => {
  let snsTypes = {}
  if (post.value.facebookPages.length) {
    snsTypes.facebook = 'Facebook'
  }
  if (post.value.instagramBusinessAccounts.length) {
    snsTypes.instagram = 'Instagram'
  }
  if (post.value.twitterUserProfiles.length) {
    snsTypes.twitter = 'X'
  }
  return snsTypes
})

const displayedAccounts = computed(() => {
  switch (selectedSnsType.value) {
  case 'facebook':
    return post.value.facebookPages
  case 'instagram':
    return post.value.instagramBusinessAccounts
  case 'twitter':
    return post.value.twitterUserProfiles
  default:
    return []
  }
})

const modalWidth = computed(() => {
  if (!props.isPreview && (submitActionModalType.value === 'approve' || submitActionModalType.value === 'request')) {
    return '80%'
  } else {
    return '50%'
  }
})
const isSelectedAccounts = computed(() => !!post.value.facebookPages.length || !!post.value.instagramBusinessAccounts.length || !!post.value.twitterUserProfiles.length)

const showTabList = computed(() => selectedPostSnsType.value === 'MULTI' && isSelectedAccounts.value)

const clickMenu = async (key) => {
  selectedSnsType.value = key
}

watch(
  previewLoading, (value) => {
    if(!value) {
      selectedSnsType.value = Object.keys(previewSnsTypes.value)[0]
      // scrollToEnd()
    }
  }
)

const closeActionModal = () => {
  emit('close-action-modal')
}

const newPostSubmit = (reservationDateTime) => {
  updateApprovalRequestNotes(approvalRequestNotes.value)
  emit('new-post-submit', reservationDateTime)
}

const postApprovalSubmit = (isApprove) => {
  updateApprovalRequestNotes(approvalRequestNotes.value)
  emit('post-approval-submit', isApprove)
}

const formatDate = (dateStr) => {
  return moment(dateStr).format(t('format.datetime.default'))
}

const noteHeader = (comment) => {
  let category = 'applicantFormat'

  if(comment.commenterName === '') {
    category = 'batchRejectFormat'
  } else if (comment.kind === 'approver_comment') {
    category = 'approverFormat'
  }

  return t(`pages.CompaniesStoreSnsPost.form.actionModal.comment.${category}`, { commentedAt: formatDate(comment.commentedAt), commenterName: comment.commenterName })
}

const noteBodyClassName = (comment) => {
  if(comment.commenterName === '') {
    return 'comment--system'
  } else if (comment.kind === 'approver_comment') {
    return 'comment--approver'
  } else if (comment.kind === 'applicant_comment') {
    return 'comment--applicant'
  }
}

const noteBodyComment = (comment) => {
  if (comment.body.length === 0) {
    if (comment.kind === 'applicant_comment') {
      return t('pages.CompaniesStoreSnsPost.comment.requestedApproval')
    } else {
      return t('pages.CompaniesStoreSnsPost.comment.revertedApproval')
    }
  }
  return comment.body
}

const load = () => {
  moment.locale(locale.value)
  selectedSnsType.value = Object.keys(previewSnsTypes.value)[0]
}

load()
</script>

<template>
  <KkModal :is-visible="props.showActionModal" :width="modalWidth" @change="closeActionModal()">
    <div class="p-modal__content__header">{{ !props.isPreview && (submitActionModalType === 'approve' || submitActionModalType === 'request') ? t('pages.CompaniesStoreSnsPost.form.actionModal.apply') : t('labels.preview') }}</div>
    <div v-if="showTabList" class="content__tab">
      <KkTabList
        :menu-list="previewSnsTypes"
        :selected-tab-key="selectedSnsType"
        @click-menu="clickMenu($event)"
      />
    </div>
    <KkLoading v-if="previewLoading" />
    <div v-else class="content__body">
      <div class="postContent" :class="`postContent__${!props.isPreview && (submitActionModalType === 'approve' || submitActionModalType === 'request') ? 'half' : 'full' }`">
        <div v-if="reservationDateTime && (isPreview || postTiming === 'reserve')" class="action__info">
          <div class="action__info-scheduleDateTime">{{ t('pages.CompaniesStoreSnsPost.form.actionModal.reservationDate') }}：<span>{{ scheduleDateTime }}</span></div>
        </div>
        <KkFacebookPreview v-if="selectedSnsType === 'facebook'" class="preview" :text="post.text" :images="post.images" :movie="post.movie" />
        <KkInstagramPreview v-if="selectedSnsType === 'instagram'" class="preview" :text="post.text" :images="post.images" :movie="post.movie" />
        <KkTwitterPreview v-if="selectedSnsType === 'twitter'" class="preview" :text="post.text" :images="post.images" :movie="post.movie" />
        <div v-if="isSelectedAccounts" class="accountList">
          <p class="accountList__title">{{ t('pages.CompaniesStoreSnsPost.form.actionModal.account') }}</p>
          <KkAccountContainer
            :accounts="displayedAccounts"
          />
        </div>
      </div>
      <div v-if="!props.isPreview && (submitActionModalType === 'approve' || submitActionModalType === 'request')" class="approvalContent">
        <h4>{{ t('pages.CompaniesStoreSnsPost.form.actionModal.approvalHistory') }}</h4>
        <div id="scroll" class="approvalHistoryContainer">
          <div v-if="post.comments.length === 0">
            <h4>{{ t('pages.CompaniesStoreSnsPost.form.actionModal.emptyApprovalHistory') }}</h4>
          </div>
          <div v-for="(comment, i) in post.comments" :key="i" class="note">
            <h4>
              {{ noteHeader(comment) }}
            </h4>
            <div class="comment" :class="noteBodyClassName(comment)">{{ noteBodyComment(comment) }}</div>
          </div>
        </div>
        <div>
          <h4>
            {{ t('pages.CompaniesStoreSnsPost.form.actionModal.approvalComment', { count: approvalRequestNotes.length, max: 400}) }}
            {{ t('labels.optionalLabel') }}
          </h4>

          <textarea v-model="approvalRequestNotes" cols="30" rows="4" maxlength="400" class="c-textArea" />
        </div>
      </div>
    </div>

    <div class="p-modal__content__footer footer">
      <div class="buttonGroup">
        <div class="buttonGroup__item">
          <KkButton color="secondary" outlined :text="t('labels.cancel')" @click="closeActionModal" />
        </div>
        <template v-if="!props.isPreview">
          <template v-if="submitActionModalType === 'approve'">
            <div class="buttonGroup__item">
              <KkButton
                color="primary"
                :text="t('pages.CompaniesStoreSnsPost.form.actionModal.rejectPost')"
                @click.once="postApprovalSubmit(false)"
              />
            </div>
            <div class="buttonGroup__item">
              <KkButton
                color="primary"
                :text="t('pages.CompaniesStoreSnsPost.form.actionModal.approvePost')"
                @click.once="postApprovalSubmit(true)"
              />
            </div>
          </template>
          <template v-else>
            <div class="buttonGroup__item">
              <KkButton :text="submitButtonText" color="primary" @click.once="newPostSubmit(true)" />
            </div>
          </template>
        </template>
      </div>
    </div>
  </KkModal>
</template>

<style lang="scss" scoped>
.action {
  &__info {
    display: flex;
    margin: 0 0 28px 0;
    justify-content: center;
    span {
      font-weight: bold;
    }

    &-scheduleDateTime {
      margin-right: 32px;
    }
  }
}
.content {
  &__body {
    height: 50vh;
    overflow-y: scroll;
    display: flex;
    justify-content: center;
    .preview {
      width: 70%;
      margin: 0 auto;
    }
  }
}
.postContent {
  height: 100%;
  padding: 32px;
  overflow-y: scroll;

  &__full {
    width: 100%;
  }

  &__half {
    width: 50%;
  }
  .accountList {
    margin-top: 32px;
    &__title {
      font-weight: bold;
      font-size: 14px;
      margin-bottom: 16px;
    }
  }
}
.approvalContent {
  width: 50%;
  height: 100%;
  background-color: #F8F9FA;
  padding: 32px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  h4 {
    margin: 0;
  }
}
.approvalHistoryContainer {
  background-color: #FFFFFF;
  border: 1px solid #E3E3E3;
  box-sizing: border-box;
  border-radius: 4px;
  height: 65%;
  overflow-y: scroll;
  box-shadow: 0 0 0 rgba(63, 63, 68, .05);

  .note {
    margin: 16px;
    padding: 0 0 10px;

    h4 {
      margin: 0;
    }

    .comment {
      margin-top: 6px;
      padding: 8px 16px;
      border-radius: 16px;
      font-size: 14px;
      line-height: 24px;
      width: fit-content;
      word-break: break-all;

      &--applicant {
        background-color: #ECEDF0;
      }

      &--approver {
        background-color: #46555F;
        color: #F8F9FA;
      }

      &--system {
        background-color: #FFFFFF;
        padding: 0;
      }
    }
  }
}

.footer {
  display: flex;
  justify-content: flex-end;
}

.buttonGroup {
  display: flex;

  &__item {
    margin: 8px;
  }
}
</style>
